import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Skeleton, Tab, Tabs, TextField, Tooltip, Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { notify } from '../../../notifications';
import { apiError } from '../../Documents/format';
import { useTimesheetRun } from '../hooks/useTimesheetRun';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { TimesheetSettings } from '../types';
import { extractFolderId, isFolderId, syncMessage } from '../utils';
import CyclePanel from './CyclePanel';
import OperationLog from './OperationLog';
import ReasonDialog from './ReasonDialog';
import RunHistory from './RunHistory';
import RunPanel from './RunPanel';

interface Props {
  open: boolean;
  folderConfigured: boolean;
  /** Годовой цикл доступен: настройки табелей и зона данных «вся сеть» */
  canCycle: boolean;
  runCtl: ReturnType<typeof useTimesheetRun>;
  onClose: () => void;
  /** Папка, год или реестр изменились: страница обновляет сводку, список и состояние раздела */
  onChanged: () => void;
}

type TabId = 'main' | 'cycle' | 'log';
type Confirm = 'save' | 'create' | null;

const BORDER = '#e2e8f0';
const sectionLabel = { mb: 1, fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 } as const;
const tabSx = { textTransform: 'none', fontWeight: 600, minHeight: 44 } as const;

/**
 * Настройки табелей. Вкладки: «Табели» (папка и год, создание, сверка, история запусков),
 * «Автоматизация» (годовой цикл и расписание, только при праве на всю сеть), «Журнал операций».
 */
const TimesheetSettingsDialog: React.FC<Props> = ({ open, folderConfigured, canCycle, runCtl, onClose, onChanged }) => {
  const [tab, setTab] = useState<TabId>('main');
  /** Открытые хотя бы раз вкладки остаются в дереве: несохранённые правки не теряются при переключении */
  const [visited, setVisited] = useState<Record<TabId, boolean>>({ main: true, cycle: false, log: false });
  const [settings, setSettings] = useState<TimesheetSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadToken, setLoadToken] = useState(0);
  const [opsToken, setOpsToken] = useState(0);
  const [folderInput, setFolderInput] = useState('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [syncing, setSyncing] = useState(false);

  const { run, running, starting } = runCtl;

  useEffect(() => {
    if (open) {
      setTab('main');
      setVisited({ main: true, cycle: false, log: false });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setSettings(null);
    setLoadError(null);
    timesheetApi.getSettings(controller.signal)
      .then(data => {
        setSettings(data);
        setFolderInput(data.folderId);
        setYear(data.year);
      })
      .catch(err => { if (!isCancelled(err)) setLoadError(apiError(err, 'Не удалось загрузить настройки')); });
    return () => controller.abort();
  }, [open, loadToken]);

  const folderId = useMemo(() => extractFolderId(folderInput), [folderInput]);
  const folderValid = isFolderId(folderId);
  const folderChanged = Boolean(settings) && folderId !== (settings as TimesheetSettings).folderId;
  const dirty = Boolean(settings) && (folderChanged || year !== (settings as TimesheetSettings).year);
  const busy = syncing || starting;

  const selectTab = useCallback((_: unknown, value: TabId) => {
    setTab(value);
    setVisited(prev => (prev[value] ? prev : { ...prev, [value]: true }));
  }, []);

  const closeConfirm = useCallback(() => setConfirm(null), []);

  const submitSave = useCallback(async (reason: string) => {
    const res = await timesheetApi.saveSettings(folderId, year, reason);
    setSettings(prev => (prev ? { ...prev, folderId: res.folderId, year: res.year } : prev));
    setFolderInput(res.folderId);
    setOpsToken(t => t + 1);
    notify.success(res.changed ? `Настройки сохранены${res.folderName ? `: папка «${res.folderName}»` : ''}` : 'Настройки не изменились');
    if (res.changed) onChanged();
  }, [folderId, year, onChanged]);

  const submitCreate = useCallback(async (reason: string) => {
    await runCtl.start(reason);
    setOpsToken(t => t + 1);
  }, [runCtl]);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await timesheetApi.sync();
      notify.success(syncMessage(result));
      onChanged();
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось сверить реестр с папкой'));
    } finally {
      setSyncing(false);
    }
  }, [onChanged]);

  /** Операция цикла выполнена: журнал перечитывается; при переключении года обновляются и настройки, и страница */
  const handleCycleDone = useCallback((kind: 'settings' | 'prepare' | 'switch') => {
    setOpsToken(t => t + 1);
    if (kind === 'switch') {
      setLoadToken(t => t + 1);
      onChanged();
    }
  }, [onChanged]);

  const createDisabled = !folderConfigured || dirty || running || busy;
  const createHint = !folderConfigured
    ? 'Сначала укажите папку Google Drive'
    : dirty ? 'Сначала сохраните изменения папки и года' : running ? 'Создание уже идёт' : '';

  const runKey = run ? `${run.id}:${run.status}` : '';
  const refreshKey = `${runKey}:${opsToken}`;
  const activeTab: TabId = tab === 'cycle' && !canCycle ? 'main' : tab;
  const hide = (id: TabId) => ({ display: activeTab === id ? 'block' : 'none' });

  return (
    <>
      <Dialog
        open={open} onClose={syncing ? undefined : onClose} maxWidth={false} scroll="paper"
        PaperProps={{ sx: { width: '100%', maxWidth: 900, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
          <Typography variant="subtitle1" fontWeight={700}>Настройки табелей</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Закрыть"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <Box sx={{ px: 2.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Tabs value={activeTab} onChange={selectTab} sx={{ minHeight: 44 }}>
            <Tab value="main" label="Табели" sx={tabSx} />
            {canCycle && <Tab value="cycle" label="Автоматизация" sx={tabSx} />}
            <Tab value="log" label="Журнал операций" sx={tabSx} />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 2.5, minHeight: 460 }}>
          <Box sx={{ display: activeTab === 'main' ? 'flex' : 'none', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography sx={sectionLabel}>Папка и год</Typography>
              {loadError ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setLoadToken(t => t + 1)}>Повторить</Button>}>
                  {loadError}
                </Alert>
              ) : !settings ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <TextField
                    size="small" label="Папка Google Drive" value={folderInput} sx={{ flex: '1 1 340px' }}
                    onChange={e => setFolderInput(e.target.value)} disabled={running || busy}
                    error={Boolean(folderInput.trim()) && !folderValid}
                    helperText={
                      folderInput.trim() && !folderValid
                        ? 'Укажите ссылку на папку или её идентификатор'
                        : folderChanged ? 'Для новой папки учёт табелей начнётся заново: создайте их или выполните сверку' : 'Ссылка на папку или её идентификатор'
                    }
                    inputProps={{ maxLength: 300 }}
                  />
                  <TextField
                    select size="small" label="Год" value={year} sx={{ width: 110 }}
                    onChange={e => setYear(Number(e.target.value))} disabled={running || busy}
                  >
                    {settings.yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </TextField>
                  <Button
                    variant="contained" onClick={() => setConfirm('save')} disabled={!dirty || !folderValid || running || busy}
                    sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                  >
                    Сохранить
                  </Button>
                </Box>
              )}
            </Box>

            <Box>
              <Typography sx={sectionLabel}>Создание табелей</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                Табели создаются из шаблона аптекам вашей зоны, у которых их ещё нет. Уже созданные файлы не затрагиваются.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Tooltip title={createHint}>
                  <span>
                    <Button
                      variant="contained" color="success" onClick={() => setConfirm('create')} disabled={createDisabled}
                      startIcon={starting ? <CircularProgress size={16} color="inherit" /> : undefined}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Создать недостающие табели
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={!folderConfigured ? 'Сначала укажите папку Google Drive' : 'Сопоставить файлы в папке с аптеками: найдёт созданные вручную и уберёт удалённые'}>
                  <span>
                    <Button
                      variant="outlined" onClick={sync} disabled={!folderConfigured || dirty || running || busy}
                      startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : undefined}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Сверить с папкой
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <RunPanel run={run} loaded={runCtl.loaded} cancelling={runCtl.cancelling} onCancel={runCtl.cancel} />
            </Box>

            <Box>
              <Typography sx={sectionLabel}>История запусков</Typography>
              <RunHistory refreshKey={refreshKey} />
            </Box>
          </Box>

          {canCycle && visited.cycle && (
            <Box sx={hide('cycle')}>
              <CyclePanel runCtl={runCtl} refreshKey={refreshKey} onDone={handleCycleDone} />
            </Box>
          )}

          {visited.log && (
            <Box sx={hide('log')}>
              <OperationLog refreshKey={refreshKey} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ py: 1.5, px: 2.5, borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ReasonDialog
        open={confirm === 'save'} title="Сохранить папку и год" confirmLabel="Сохранить"
        description="Смена папки или года влияет на все табели раздела. Прежнее и новое значения попадут в журнал операций."
        onClose={closeConfirm} onSubmit={submitSave}
      />
      <ReasonDialog
        open={confirm === 'create'} title="Создать недостающие табели" confirmLabel="Создать" confirmColor="success"
        description="Табели будут созданы аптекам вашей зоны, у которых их ещё нет."
        onClose={closeConfirm} onSubmit={submitCreate}
      />
    </>
  );
};

export default TimesheetSettingsDialog;
